import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api from '../../api/workTasksApi';
import { priorities, sourceLabel, sourceUrl, statuses, taskDate, taskError, zurichInput, zurichUtc } from './taskUtils';

const empty = { title: '', description: '', assignee_user_id: '', status: 'open', priority: 'normal', due_at: '', checklist: [] };
const draftOf = task => ({ title: task.title, description: task.description || '', assignee_user_id: task.assignee_user_id || '', status: task.status, priority: task.priority, due_at: zurichInput(task.due_at), checklist: task.checklist || [] });

export default function WorkTaskEditor({ open, taskId = null, sources = [], initialTitle = '', onClose, onSaved }) {
  const [task, setTask] = useState(null);
  const [draft, setDraft] = useState(empty);
  const [permissions, setPermissions] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [events, setEvents] = useState({ items: [], total: 0 });
  const [eventPage, setEventPage] = useState(1);
  const [comment, setComment] = useState('');
  const [checkTitle, setCheckTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [backup, setBackup] = useState(null);
  const operation = useRef(null);
  const sequence = useRef(0);
  const seedKey = JSON.stringify(sources);
  const can = action => permissions.includes(`work_tasks.${action}`);

  const load = useCallback(async () => {
    const seq = ++sequence.current;
    setLoading(true); setError('');
    try {
      const caps = await api.capabilities();
      const record = taskId ? await api.get(taskId) : null;
      const refs = record?.sources || JSON.parse(seedKey);
      const people = caps.permissions.includes('work_tasks.assign') ? await api.staff(refs) : { items: [] };
      const history = record ? await api.events(taskId) : { items: [], total: 0 };
      if (seq !== sequence.current) return;
      setTask(record); setPermissions(caps.permissions); setCurrentUserId(caps.user_id); setStaff(people.items);
      setDraft(record ? draftOf(record) : { ...empty, title: initialTitle.slice(0, 255) });
      setEvents(history); setEventPage(1); setConflict(false); operation.current = null;
    } catch (e) {
      if (seq === sequence.current) { setTask(null); setPermissions([]); setError(taskError(e)); }
    } finally { if (seq === sequence.current) setLoading(false); }
  }, [taskId, seedKey, initialTitle]);

  useEffect(() => {
    const activeRequests = sequence;
    if (open) { setBackup(null); setComment(''); load(); }
    return () => { ++activeRequests.current; };
  }, [open, load]);
  const change = (field, value) => setDraft(previous => ({ ...previous, [field]: value }));
  const refs = task?.sources || sources;
  const bodyId = body => {
    const key = JSON.stringify(body);
    if (operation.current?.key !== key) operation.current = { key, id: uuid() };
    return { ...body, operation_id: operation.current.id };
  };
  const mutationError = e => {
    setError(taskError(e));
    if (e?.response?.status === 409) setConflict(true);
    if ([401, 403, 404].includes(e?.response?.status)) { setTask(null); setPermissions([]); setBackup(null); }
  };
  const save = async () => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const body = { ...draft, title: draft.title.trim(), description: draft.description || null, due_at: zurichUtc(draft.due_at), assignee_user_id: draft.assignee_user_id || null };
      if (task) body.expected_revision = task.revision;
      else { delete body.status; body.sources = refs; }
      const result = task ? await api.update(task.id, bodyId(body)) : await api.create(bodyId(body));
      onSaved?.(result); onClose();
      window.dispatchEvent(new Event('work-tasks-changed'));
    } catch (e) { mutationError(e); } finally { setSaving(false); }
  };
  const addComment = async () => {
    setSaving(true); setError('');
    try {
      const result = await api.comment(task.id, bodyId({ expected_revision: task.revision, body: comment.trim() }));
      setTask(result); setComment(''); setEvents(await api.events(task.id)); setEventPage(1); operation.current = null;
      onSaved?.(result);
    } catch (e) { mutationError(e); } finally { setSaving(false); }
  };
  const olderEvents = async () => {
    setSaving(true);
    try {
      const history = await api.events(task.id, eventPage + 1);
      setEvents(previous => ({ ...history, items: [...previous.items, ...history.items] })); setEventPage(previous => previous + 1);
    } catch (e) { mutationError(e); } finally { setSaving(false); }
  };
  const sourceMutation = async (source, remove = false) => {
    setSaving(true); setError('');
    try {
      const result = await api[remove ? 'unlink' : 'link'](task.id, bodyId({ expected_revision: task.revision, source }));
      setTask(result); setEvents(await api.events(task.id)); setEventPage(1); operation.current = null;
      if (can('assign')) setStaff((await api.staff(result.sources)).items);
      onSaved?.(result);
    } catch (e) { mutationError(e); } finally { setSaving(false); }
  };
  const editable = task ? can('update') && (can('manage') || [task.assignee_user_id, task.created_by_user_id].includes(currentUserId)) : can('create');
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
    <DialogTitle>{taskId ? 'Aufgabe' : 'Aufgabe erstellen'}</DialogTitle>
    <DialogContent dividers>
      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}
      {conflict && <Alert severity="warning" sx={{ mb: 2 }}>Ihre Eingaben bleiben erhalten. Laden Sie den aktuellen Stand vor einem erneuten Speichern.
        <Button onClick={() => { setBackup(draft); load(); }}>Aktuellen Stand laden</Button>
      </Alert>}
      {backup && !loading && <Button onClick={() => { setDraft(backup); setBackup(null); }}>Meine vorherigen Eingaben wiederherstellen</Button>}
      {loading ? <CircularProgress aria-label="Aufgabe laden" /> : permissions.includes('work_tasks.read') && <Stack spacing={2}>
        <TextField label="Titel" required value={draft.title} onChange={e => change('title', e.target.value)} inputProps={{ maxLength: 255 }} disabled={!editable} />
        <TextField label="Beschreibung" multiline minRows={3} value={draft.description} onChange={e => change('description', e.target.value)} inputProps={{ maxLength: 10000 }} disabled={!editable} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label="Zuständig" value={draft.assignee_user_id} onChange={e => change('assignee_user_id', e.target.value)} disabled={!editable || !can('assign')} fullWidth>
            <MenuItem value="">Unzugewiesen</MenuItem>
            {draft.assignee_user_id && !staff.some(p => p.id === draft.assignee_user_id) && <MenuItem value={draft.assignee_user_id}>Bisherige Zuweisung (nicht neu zuweisbar)</MenuItem>}
            {staff.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          <TextField select label="Priorität" value={draft.priority} onChange={e => change('priority', e.target.value)} disabled={!editable} fullWidth>{Object.entries(priorities).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField>
          {task && <TextField select label="Status" value={draft.status} onChange={e => change('status', e.target.value)} disabled={!editable} fullWidth>{Object.entries(statuses).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField>}
        </Stack>
        <TextField label="Frist (Europe/Zurich)" type="datetime-local" InputLabelProps={{ shrink: true }} value={draft.due_at} onChange={e => change('due_at', e.target.value)} disabled={!editable} helperText="Bei der doppelten Herbststunde gilt das erste Auftreten." />
        <Box><Typography fontWeight={700}>Checkliste</Typography>
          {draft.checklist.map(item => <Stack key={item.id} direction="row" alignItems="center">
            <Checkbox checked={item.done} inputProps={{ 'aria-label': item.title }} disabled={!editable} onChange={e => change('checklist', draft.checklist.map(row => row.id === item.id ? { ...row, done: e.target.checked } : row))} />
            <Typography sx={{ flex: 1 }}>{item.title}</Typography>
            {editable && <Button aria-label={`${item.title} entfernen`} onClick={() => change('checklist', draft.checklist.filter(row => row.id !== item.id))}>Entfernen</Button>}
          </Stack>)}
          {editable && <Stack direction="row" spacing={1}><TextField size="small" label="Neuer Checklistenpunkt" value={checkTitle} inputProps={{ maxLength: 200 }} onChange={e => setCheckTitle(e.target.value)} />
            <Button disabled={!checkTitle.trim() || draft.checklist.length >= 80} onClick={() => { change('checklist', [...draft.checklist, { id: uuid(), title: checkTitle.trim(), done: false }]); setCheckTitle(''); }}>Hinzufügen</Button></Stack>}
        </Box>
        <Box><Typography fontWeight={700}>Verknüpfte Quellen</Typography>{refs.length ? refs.map(source => <Box key={`${source.kind}:${source.source_id}`}>
          <Button href={sourceUrl(source)}>{source.kind === 'meeting' ? 'Meeting-Konversation öffnen' : `${sourceLabel(source)} öffnen`}</Button>
          {task && can('manage') && <Button disabled={saving || conflict} onClick={() => sourceMutation(source, true)}>Verknüpfung entfernen</Button>}
        </Box>) : <Typography color="text.secondary">Eigenständige Teamaufgabe</Typography>}
          {task && editable && sources.filter(seed => !refs.some(ref => ref.kind === seed.kind && ref.source_id === seed.source_id)).map(seed => <Button key={`${seed.kind}:${seed.source_id}`} disabled={saving || conflict} onClick={() => sourceMutation(seed)}>{sourceLabel(seed)} verknüpfen</Button>)}
        </Box>
        {task && <>
          <Typography variant="caption">Erstellt {taskDate(task.created_at)} · Aktualisiert {taskDate(task.updated_at)} · Version {task.revision}</Typography>
          <Typography fontWeight={700}>Interner Verlauf</Typography>
          {events.items.map(event => <Box key={event.id} sx={{ borderLeft: '2px solid #32cd32', pl: 1 }}>
            <Typography variant="caption">{taskDate(event.created_at)} · {staff.find(person => person.id === event.author_user_id)?.name || (event.author_user_id === currentUserId ? 'Von mir' : 'Teammitglied')}</Typography>
            <Typography>{event.kind === 'comment_added' ? event.data.body : ({ created: 'Aufgabe erstellt', updated: 'Aufgabe geändert', source_linked: 'Quelle verknüpft', source_removed: 'Quelle entfernt', legacy_imported: 'Bestehende Aufgabe übernommen' }[event.kind] || event.kind)}</Typography>
            {event.data.fields && <Typography variant="caption">{event.data.fields.map(field => ({ status: 'Status', assignee_user_id: 'Zuständigkeit', title: 'Titel', description: 'Beschreibung', priority: 'Priorität', due_at: 'Frist', checklist: 'Checkliste', completed_at: 'Abschlusszeit' }[field])).filter(Boolean).join(', ')}</Typography>}
          </Box>)}
          {events.items.length < events.total && <Button disabled={saving} onClick={olderEvents}>Ältere Einträge laden</Button>}
          {editable && <><TextField label="Interner Kommentar" multiline value={comment} inputProps={{ maxLength: 5000 }} onChange={e => setComment(e.target.value)} />
            <Button disabled={saving || conflict || !comment.trim()} onClick={addComment}>Kommentar speichern</Button></>}
        </>}
      </Stack>}
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Schliessen</Button><Button variant="contained" onClick={save} disabled={loading || saving || !editable || conflict || !draft.title.trim()}>Speichern</Button></DialogActions>
  </Dialog>;
}

WorkTaskEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  taskId: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string.isRequired,
    source_id: PropTypes.string.isRequired,
    conversation_id: PropTypes.string,
  })),
  initialTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
